"use client";

import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import { toHtml } from "hast-util-to-html";

// Styles
import cn from "classnames";
import s from "./RichText.module.scss";
import { preprocessContent, flattenP, flattenPToBR, formatList } from "./helpers";
import { Link } from "./Link";
import { SITE_DOMAIN } from "@/shared/config/constants";
import { omit } from "lodash";

export type RichTextProps = {
  content?: string;
  className?: string;
};

// Note: Only lowercase components names are supported
const components = {
  a: ({ node, className, ...props }) => {
    if (!props?.href) {
      return <span className={className} {...props} />;
    }

    const isExternalLink =
      (!props?.href?.match(new RegExp(SITE_DOMAIN, "img")) || "blank" in props) &&
      !props?.href?.startsWith("/");

    const href = props?.href;

    return (
      <Link
        className={cn(className, "link")}
        target={isExternalLink ? "_blank" : "_self"}
        {...props}
        href={href}
      />
    );
  },
  div: ({ node, ...props }) => {
    return <div {...props} />;
  },
  pre: ({ node, ...props }) => {
    if (node.children.length === 1 && node.children[0].tagName === "code") {
      return <div dangerouslySetInnerHTML={{ __html: node.children[0].children[0].value }} />;
    }

    return <pre {...props} />;
  },
  table: ({ node, ...props }) => {
    return (
      <div className="table-wrap">
        <table
          {...omit(props, "children")}
          dangerouslySetInnerHTML={{ __html: flattenP(toHtml(node.children)) }}
        />
      </div>
    );
  },
  ul: ({ node, ...props }) => {
    const blockType = props["data-type"];

    return (
      <ul
        {...omit(props, "children")}
        dangerouslySetInnerHTML={{ __html: formatList(flattenP(toHtml(node.children))) }}
      />
    );
  },
  ol: ({ node, ...props }) => (
    <ol
      {...omit(props, "children")}
      dangerouslySetInnerHTML={{ __html: formatList(flattenP(toHtml(node.children))) }}
    />
  ),
  blockquote: ({ node, ...props }) => (
    <blockquote
      {...omit(props, "children")}
      dangerouslySetInnerHTML={{ __html: flattenP(flattenPToBR(toHtml(node.children))) }}
    />
  ),
};

/**
 * Shared.RichText
 * Supports markdown, gfm, raw html + custom components
 *
 * ¯\_(ツ)_/¯
 * NOTE:
 *      This component always wraps content into <p> (paragraph)
 *      If you need to render without <p> use component <HTML> ->
 *      it supports only raw html, and wraps content into <span>
 *
 * @relation StrapiSharedRichText
 * @param content
 * @param className
 * @constructor
 */
export const RichText = ({ content, className }: RichTextProps) => {
  if (!content) return null;

  return (
    <div className={cn(className, s.content)}>
      <Markdown
        remarkRehypeOptions={{ allowDangerousHtml: true }}
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        //@ts-ignore
        components={components}
      >
        {preprocessContent(content)}
      </Markdown>
    </div>
  );
};
